import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clock,
  Coins,
  KeyRound,
  Lock,
  PlayCircle,
  RefreshCw,
  Save,
  ShieldCheck,
  ToggleLeft,
  ToggleRight,
  Users,
} from 'lucide-react';
import { api } from '../utils/api';
import './AgentControlCenter.css';

const RELEASE_MODES = ['PRIVATE', 'DARK_RELEASE', 'ALLOWLIST_BETA', 'PUBLIC_BETA', 'GA'];
const CREDIT_SOURCES = ['TRIAL', 'PROMO', 'PLAN', 'ADMIN', 'REFUND', 'ADJUSTMENT'];

const AgentControlCenter = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [summary, setSummary] = useState(null);
  const [flags, setFlags] = useState([]);
  const [controls, setControls] = useState([]);
  const [runs, setRuns] = useState([]);
  const [usage, setUsage] = useState(null);
  const [releaseMode, setReleaseMode] = useState('PRIVATE');
  const [updatingControlKey, setUpdatingControlKey] = useState('');
  const [merchantAccess, setMerchantAccess] = useState({ merchantId: '', enabled: true, reason: '' });
  const [creditGrant, setCreditGrant] = useState({
    merchantId: '',
    creditsGranted: '250',
    source: 'TRIAL',
    expiresAt: '',
    reason: 'Agent trial credits',
  });
  const [flagForm, setFlagForm] = useState({
    key: 'agent.mutation_execution',
    value: '{\n  "enabled": false\n}',
    merchantId: '',
    tier: '',
    description: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [summaryRes, flagsRes, runsRes, usageRes, controlsRes] = await Promise.all([
        api.get('/admin/agent/summary'),
        api.get('/admin/agent/feature-flags'),
        api.get('/admin/agent/runs?take=25&timeRange=30d'),
        api.get('/admin/agent/usage?timeRange=30d'),
        api.get('/admin/agent/controls'),
      ]);

      setSummary(summaryRes.data);
      setReleaseMode(summaryRes.data?.releaseMode || 'PRIVATE');
      setFlags(flagsRes.data?.flags || []);
      setRuns(runsRes.data?.runs || []);
      setUsage(usageRes.data || null);
      setControls(controlsRes.data?.controls || []);
    } catch (err) {
      setError(err.message || 'Failed to load Agent controls');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const statusCounts = useMemo(() => summary?.last30Days?.runsByStatus || {}, [summary]);
  const byokTotal = useMemo(() => {
    const credentials = summary?.byokCredentials || {};
    return Object.values(credentials).reduce((total, count) => total + Number(count || 0), 0);
  }, [summary]);
  const enabledControlCount = useMemo(
    () => controls.filter((control) => control.enabled).length,
    [controls]
  );

  const saveReleaseMode = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/admin/agent/release-mode', {
        mode: releaseMode,
        description: 'Updated from Agent Control Center',
      });
      setSuccess(`Agent release mode set to ${releaseMode}.`);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update release mode');
    } finally {
      setSaving(false);
    }
  };

  const toggleControl = async (control, nextEnabled) => {
    setUpdatingControlKey(control.key);
    setError('');
    setSuccess('');
    try {
      const response = await api.put(`/admin/agent/controls/${encodeURIComponent(control.key)}`, {
        enabled: nextEnabled,
        reason: nextEnabled ? 'Enabled from Agent Control Center' : 'Disabled from Agent Control Center',
      });
      setControls(response.data?.controls?.controls || []);
      setSuccess(`${control.label} ${nextEnabled ? 'enabled' : 'disabled'}.`);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update Agent control');
    } finally {
      setUpdatingControlKey('');
    }
  };

  const updateMerchantAccess = async () => {
    if (!merchantAccess.merchantId.trim()) {
      setError('Merchant ID is required.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/admin/agent/merchants/${merchantAccess.merchantId.trim()}/access`, {
        enabled: merchantAccess.enabled,
        reason: merchantAccess.reason,
      });
      setSuccess(merchantAccess.enabled ? 'Merchant allowlisted for Agent.' : 'Merchant Agent access disabled.');
      setMerchantAccess({ merchantId: '', enabled: true, reason: '' });
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update merchant access');
    } finally {
      setSaving(false);
    }
  };

  const createCreditGrant = async () => {
    if (!creditGrant.merchantId.trim()) {
      setError('Merchant ID is required for credit grants.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/admin/agent/credit-grants', {
        ...creditGrant,
        merchantId: creditGrant.merchantId.trim(),
        creditsGranted: Number(creditGrant.creditsGranted),
        expiresAt: creditGrant.expiresAt || null,
      });
      setSuccess('Agent credits granted.');
      setCreditGrant({
        merchantId: '',
        creditsGranted: '250',
        source: 'TRIAL',
        expiresAt: '',
        reason: 'Agent trial credits',
      });
      await load();
    } catch (err) {
      setError(err.message || 'Failed to grant credits');
    } finally {
      setSaving(false);
    }
  };

  const saveFlag = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let value;
      try {
        value = JSON.parse(flagForm.value);
      } catch (_err) {
        throw new Error('Flag value must be valid JSON.');
      }

      await api.post('/admin/agent/feature-flags', {
        key: flagForm.key,
        value,
        merchantId: flagForm.merchantId || null,
        tier: flagForm.tier || null,
        description: flagForm.description || null,
      });
      setSuccess('Agent flag saved.');
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save flag');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="agc-page">
      <header className="agc-header">
        <div>
          <h1><Bot size={24} /> Agent Control Center</h1>
          <p>Control Revstar Agent rollout, trial credits, BYOK adoption, dry-run safety, and run health.</p>
        </div>
        <button type="button" className="agc-btn agc-btn-secondary" onClick={load} disabled={loading || saving}>
          <RefreshCw size={15} className={loading ? 'agc-spin' : ''} />
          <span>Refresh</span>
        </button>
      </header>

      {error && <Banner tone="danger" icon={<AlertTriangle size={15} />} text={error} />}
      {success && <Banner tone="success" icon={<CheckCircle2 size={15} />} text={success} />}

      <section className="agc-status-grid">
        <Metric icon={<ShieldCheck />} label="Release mode" value={summary?.releaseMode || releaseMode} />
        <Metric icon={<PlayCircle />} label="Runs, 30 days" value={sumObjectValues(statusCounts)} />
        <Metric icon={<Clock />} label="Pending approvals" value={summary?.pendingApprovals || 0} />
        <Metric icon={<Coins />} label="Credits remaining" value={summary?.activeCreditGrants?.remaining || 0} />
        <Metric icon={<KeyRound />} label="BYOK credentials" value={byokTotal} />
        <Metric icon={<ToggleRight />} label="Capabilities on" value={`${enabledControlCount}/${controls.length || 0}`} />
      </section>

      <div className="agc-main-grid">
        <section className="agc-panel agc-control-panel">
          <div className="agc-panel-head">
            <h2><ToggleLeft size={18} /> Release Control</h2>
            <span>{summary?.environment || 'environment'}</span>
          </div>
          <label className="agc-field">
            <span>Mode</span>
            <select value={releaseMode} onChange={(event) => setReleaseMode(event.target.value)}>
              {RELEASE_MODES.map((mode) => (
                <option key={mode} value={mode}>{mode}</option>
              ))}
            </select>
          </label>
          <p className="agc-note">
            <code>PRIVATE</code> and <code>DARK_RELEASE</code> require explicit merchant allowlisting. <code>PUBLIC_BETA</code> and <code>GA</code> open the portal to eligible merchants while feature gates still apply.
          </p>
          <button type="button" className="agc-btn agc-btn-primary" onClick={saveReleaseMode} disabled={saving}>
            <Save size={15} />
            <span>Save Mode</span>
          </button>
        </section>

        <section className="agc-panel agc-capability-panel">
          <div className="agc-panel-head">
            <h2><ShieldCheck size={18} /> Agent Capabilities</h2>
            <span>Super Admin</span>
          </div>
          <div className="agc-control-list">
            {controls.map((control) => (
              <div key={control.key} className={`agc-control-item ${control.enabled ? 'agc-control-on' : 'agc-control-off'}`}>
                <div className="agc-control-copy">
                  <strong>{control.label}</strong>
                  <span>{control.enabled ? control.enabledDescription : control.disabledDescription}</span>
                  <code>{control.key}</code>
                </div>
                <div className="agc-control-actions">
                  <StatusPill value={control.enabled ? 'ON' : 'OFF'} />
                  <button
                    type="button"
                    className={`agc-btn ${control.enabled ? 'agc-btn-secondary' : 'agc-btn-primary'}`}
                    onClick={() => toggleControl(control, !control.enabled)}
                    disabled={saving || updatingControlKey === control.key}
                    title={`${control.enabled ? 'Disable' : 'Enable'} ${control.label}`}
                  >
                    {control.enabled ? <Lock size={15} /> : <ToggleRight size={15} />}
                    <span>{control.enabled ? 'Turn Off' : 'Turn On'}</span>
                  </button>
                </div>
              </div>
            ))}
            {!controls.length && <div className="agc-empty">No Agent capability controls loaded.</div>}
          </div>
        </section>

        <section className="agc-panel">
          <div className="agc-panel-head">
            <h2><Users size={18} /> Merchant Access</h2>
          </div>
          <div className="agc-two-col">
            <label className="agc-field">
              <span>Merchant ID</span>
              <input
                value={merchantAccess.merchantId}
                onChange={(event) => setMerchantAccess((prev) => ({ ...prev, merchantId: event.target.value }))}
                placeholder="Merchant user UUID"
              />
            </label>
            <label className="agc-field">
              <span>Access</span>
              <select
                value={merchantAccess.enabled ? 'enabled' : 'disabled'}
                onChange={(event) => setMerchantAccess((prev) => ({ ...prev, enabled: event.target.value === 'enabled' }))}
              >
                <option value="enabled">Allowlisted</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
          </div>
          <label className="agc-field">
            <span>Reason</span>
            <input
              value={merchantAccess.reason}
              onChange={(event) => setMerchantAccess((prev) => ({ ...prev, reason: event.target.value }))}
              placeholder="Pilot merchant, internal test, support session"
            />
          </label>
          <button type="button" className="agc-btn agc-btn-primary" onClick={updateMerchantAccess} disabled={saving}>
            <ShieldCheck size={15} />
            <span>Update Access</span>
          </button>
        </section>

        <section className="agc-panel">
          <div className="agc-panel-head">
            <h2><Coins size={18} /> Credit Grants</h2>
          </div>
          <div className="agc-two-col">
            <label className="agc-field">
              <span>Merchant ID</span>
              <input
                value={creditGrant.merchantId}
                onChange={(event) => setCreditGrant((prev) => ({ ...prev, merchantId: event.target.value }))}
                placeholder="Merchant user UUID"
              />
            </label>
            <label className="agc-field">
              <span>Credits</span>
              <input
                type="number"
                min="1"
                value={creditGrant.creditsGranted}
                onChange={(event) => setCreditGrant((prev) => ({ ...prev, creditsGranted: event.target.value }))}
              />
            </label>
          </div>
          <div className="agc-two-col">
            <label className="agc-field">
              <span>Source</span>
              <select
                value={creditGrant.source}
                onChange={(event) => setCreditGrant((prev) => ({ ...prev, source: event.target.value }))}
              >
                {CREDIT_SOURCES.map((source) => <option key={source} value={source}>{source}</option>)}
              </select>
            </label>
            <label className="agc-field">
              <span>Expires At</span>
              <input
                type="datetime-local"
                value={creditGrant.expiresAt}
                onChange={(event) => setCreditGrant((prev) => ({ ...prev, expiresAt: event.target.value }))}
              />
            </label>
          </div>
          <label className="agc-field">
            <span>Reason</span>
            <input
              value={creditGrant.reason}
              onChange={(event) => setCreditGrant((prev) => ({ ...prev, reason: event.target.value }))}
            />
          </label>
          <button type="button" className="agc-btn agc-btn-primary" onClick={createCreditGrant} disabled={saving}>
            <Coins size={15} />
            <span>Grant Credits</span>
          </button>
        </section>

        <section className="agc-panel">
          <div className="agc-panel-head">
            <h2><ToggleLeft size={18} /> Feature Flag</h2>
          </div>
          <div className="agc-two-col">
            <label className="agc-field">
              <span>Key</span>
              <input
                value={flagForm.key}
                onChange={(event) => setFlagForm((prev) => ({ ...prev, key: event.target.value }))}
              />
            </label>
            <label className="agc-field">
              <span>Tier</span>
              <input
                value={flagForm.tier}
                onChange={(event) => setFlagForm((prev) => ({ ...prev, tier: event.target.value }))}
                placeholder="Optional"
              />
            </label>
          </div>
          <label className="agc-field">
            <span>Merchant ID</span>
            <input
              value={flagForm.merchantId}
              onChange={(event) => setFlagForm((prev) => ({ ...prev, merchantId: event.target.value }))}
              placeholder="Optional merchant override"
            />
          </label>
          <label className="agc-field">
            <span>JSON Value</span>
            <textarea
              rows={5}
              value={flagForm.value}
              onChange={(event) => setFlagForm((prev) => ({ ...prev, value: event.target.value }))}
            />
          </label>
          <label className="agc-field">
            <span>Description</span>
            <input
              value={flagForm.description}
              onChange={(event) => setFlagForm((prev) => ({ ...prev, description: event.target.value }))}
              placeholder="Why this flag exists"
            />
          </label>
          <button type="button" className="agc-btn agc-btn-primary" onClick={saveFlag} disabled={saving}>
            <Save size={15} />
            <span>Save Flag</span>
          </button>
        </section>
      </div>

      <section className="agc-panel">
        <div className="agc-panel-head">
          <h2><PlayCircle size={18} /> Recent Agent Runs</h2>
          <span>Last 30 days</span>
        </div>
        <div className="agc-table-wrap">
          <table className="agc-table">
            <thead>
              <tr>
                <th>Merchant</th>
                <th>Mode</th>
                <th>Status</th>
                <th>Steps</th>
                <th>Artifacts</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id}>
                  <td>
                    <strong>{run.merchant?.username || run.merchant?.email || run.merchantId}</strong>
                    <span className="agc-row-sub">{run.store?.name || run.store?.slug || 'No store'}</span>
                  </td>
                  <td>{run.mode}</td>
                  <td><StatusPill value={run.status} /></td>
                  <td>{run._count?.steps || 0}</td>
                  <td>{run._count?.artifacts || 0}</td>
                  <td>{formatDate(run.createdAt)}</td>
                </tr>
              ))}
              {!runs.length && (
                <tr>
                  <td colSpan="6" className="agc-empty">No Agent runs found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="agc-main-grid agc-bottom-grid">
        <section className="agc-panel">
          <div className="agc-panel-head">
            <h2><ToggleLeft size={18} /> Active Flags</h2>
            <span>{flags.length}</span>
          </div>
          <div className="agc-flag-list">
            {flags.map((flag) => (
              <div key={flag.id} className="agc-flag-item">
                <div>
                  <strong>{flag.key}</strong>
                  <span>{flag.merchant?.email || flag.merchantId || flag.tier || 'Global'}</span>
                </div>
                <code>{compactJson(flag.value)}</code>
              </div>
            ))}
            {!flags.length && <div className="agc-empty">No Agent flags configured.</div>}
          </div>
        </section>

        <section className="agc-panel">
          <div className="agc-panel-head">
            <h2><KeyRound size={18} /> Usage</h2>
            <span>Last 30 days</span>
          </div>
          <div className="agc-usage-grid">
            <Metric label="Ledger entries" value={usage?.totals?.entries || 0} />
            <Metric label="Input tokens" value={usage?.totals?.inputTokens || 0} />
            <Metric label="Output tokens" value={usage?.totals?.outputTokens || 0} />
            <Metric label="Credits used" value={usage?.totals?.creditsUsed || 0} />
          </div>
        </section>
      </div>
    </div>
  );
};

function Banner({ tone, icon, text }) {
  return (
    <div className={`agc-banner agc-banner-${tone}`}>
      {icon}
      <span>{text}</span>
    </div>
  );
}

function Metric({ icon, label, value }) {
  return (
    <div className="agc-metric">
      {icon && <div className="agc-metric-icon">{React.cloneElement(icon, { size: 18 })}</div>}
      <span>{label}</span>
      <strong>{formatNumber(value)}</strong>
    </div>
  );
}

function StatusPill({ value }) {
  const normalized = String(value || 'UNKNOWN').toLowerCase();
  return <span className={`agc-status agc-status-${normalized}`}>{value || 'UNKNOWN'}</span>;
}

function sumObjectValues(object) {
  return Object.values(object || {}).reduce((total, value) => total + Number(value || 0), 0);
}

function formatNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toLocaleString() : String(value || 0);
}

function formatDate(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  return date.toLocaleString();
}

function compactJson(value) {
  try {
    return JSON.stringify(value);
  } catch (_err) {
    return String(value);
  }
}

export default AgentControlCenter;
